import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Input,
  Button,
  notification,
  Table,
  Tag,
  Divider,
  Spin,
  Popconfirm,
  Space,
  Tabs,
} from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { formatCurrency } from '../withdrawal-check/utils';
import useEmployee from '../../hook/useEmployee';
import PaymentService from '../../service/payment-service/Payment.service';
import { PaymentResponse as ServicePaymentResponse } from '../../model/Payment.model';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

interface WithdrawalTransaction {
  id: number;
  transactionDate: string;
  amount: number;
  feeForCustomer?: number;
  customerType?: '%' | 'vn'; // Loại phí khách hàng: % hoặc VND
  feeApplied?: number;
  commissionRateForPartner?: number; // Phí CTV (%)
  balance: number;
  status: string;
  returnToCompany: boolean;
  employee: {
    id: number;
    name: string;
    contactInfo?: string;
  };
  posDevice?: {
    id: number;
    deviceCode: string;
    location: string;
  };
  creditCard?: {
    id: number;
    cardNumber: string;
    cardHolderName?: string;
  };
}

interface PaymentResponse {
  id: number;
  amount: number;
  paymentDate: string;
  originalStatus: string;
  employee?: {
    id: number;
    name: string;
  };
  customer?: {
    id: number;
    fullName: string;
  };
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedByName?: string;
}

interface WithdrawalPaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  transaction: WithdrawalTransaction | null;
}

const WithdrawalPaymentModal: React.FC<WithdrawalPaymentModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  transaction,
}) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentResponse[]>([]);
  const [deletedPayments, setDeletedPayments] = useState<PaymentResponse[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [deletedPaymentsLoading, setDeletedPaymentsLoading] = useState(false);
  const [deletedPaymentsError, setDeletedPaymentsError] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState('active');
  const [editingPayment, setEditingPayment] = useState<PaymentResponse | null>(
    null
  );
  const [editModalVisible, setEditModalVisible] = useState(false);
  const { employee } = useEmployee();

  // Load existing payments when modal opens
  useEffect(() => {
    if (visible && transaction) {
      loadPayments();
    }
  }, [visible, transaction]);

  const loadPayments = async () => {
    if (!transaction) return;

    try {
      setPaymentsLoading(true);
      const paymentsData =
        await PaymentService.getPaymentsByWithdrawalTransaction(transaction.id);
      // Convert ServicePaymentResponse to local PaymentResponse format
      const convertedPayments = paymentsData.map((payment: any) => ({
        ...payment,
        employee: payment.employee || undefined, // Convert null to undefined
      }));
      setPayments(Array.isArray(convertedPayments) ? convertedPayments : []);
    } catch (error) {
      console.error('Error loading payments:', error);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadDeletedPayments = async () => {
    if (!transaction) return;

    try {
      setDeletedPaymentsLoading(true);
      setDeletedPaymentsError(null);
      const result =
        await PaymentService.getDeletedPaymentsByWithdrawalTransaction(
          transaction.id
        );
      if (result.success && result.data) {
        // Convert ServicePaymentResponse to local PaymentResponse format
        const convertedPayments = result.data.map((payment: any) => ({
          ...payment,
          employee: payment.employee || undefined, // Convert null to undefined
        }));
        setDeletedPayments(
          Array.isArray(convertedPayments) ? convertedPayments : []
        );
      } else {
        setDeletedPayments([]);
      }
    } catch (error) {
      console.error('Error loading deleted payments:', error);
      setDeletedPayments([]);
    } finally {
      setDeletedPaymentsLoading(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    if (key === 'deleted') {
      loadDeletedPayments();
    } else if (key === 'active') {
      loadPayments();
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      if (!transaction) {
        notification.error({
          message: 'Lỗi',
          description: 'Không tìm thấy thông tin giao dịch',
        });
        return;
      }

      if (!employee) {
        notification.error({
          message: 'Lỗi',
          description: 'Không tìm thấy thông tin nhân viên đang đăng nhập',
        });
        return;
      }

      const paymentData = {
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      await PaymentService.createWithdrawalPayment(
        transaction.id,
        paymentData,
        employee.id
      );

      notification.success({
        message: 'Thành công',
        description: 'Đã ghi nhận thanh toán cho giao dịch rút tiền',
      });

      form.resetFields();
      await loadPayments();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating payment:', error);
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Có lỗi xảy ra khi ghi nhận thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditPayment = (payment: PaymentResponse) => {
    setEditingPayment(payment);
    editForm.setFieldsValue({
      amount: payment.amount,
      paymentDate: dayjs(payment.paymentDate),
      notes: '',
    });
    setEditModalVisible(true);
  };

  const handleUpdatePayment = async () => {
    if (!editingPayment || !employee) return;

    try {
      setLoading(true);
      const values = await editForm.validateFields();

      const updateData = {
        amount: values.amount,
        paymentDate: values.paymentDate.format('YYYY-MM-DD'),
        notes: values.notes || '',
      };

      await PaymentService.updatePayment(
        editingPayment.id,
        updateData,
        employee.id
      );

      notification.success({
        message: 'Thành công',
        description: 'Đã cập nhật thông tin thanh toán',
      });

      setEditModalVisible(false);
      setEditingPayment(null);
      editForm.resetFields();
      await loadPayments();
      onSuccess();
    } catch (error: any) {
      console.error('Error updating payment:', error);
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Có lỗi xảy ra khi cập nhật thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      setLoading(true);
      await PaymentService.deletePayment(paymentId, employee?.id as number);

      notification.success({
        message: 'Thành công',
        description: 'Đã xóa thanh toán và hoàn tiền',
      });

      await loadPayments();
      if (activeTab === 'deleted') {
        loadDeletedPayments();
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error deleting payment:', error);
      notification.error({
        message: 'Lỗi',
        description: error.message || 'Có lỗi xảy ra khi xóa thanh toán',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPayments([]);
    setDeletedPayments([]);
    setActiveTab('active');
    setDeletedPaymentsError(null);
    onCancel();
  };

  const handleEditCancel = () => {
    setEditModalVisible(false);
    setEditingPayment(null);
    editForm.resetFields();
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'PENDING':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Hoàn thành';
      case 'PENDING':
        return 'Đang chờ';
      default:
        return status;
    }
  };

  // Format card number
  const formatCardNumber = (cardNumber: string) => {
    return `****${cardNumber.slice(-4)}`;
  };

  // Table columns for active payments
  const paymentColumns: ColumnsType<PaymentResponse> = [
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount: number) => (
        <span className="font-semibold text-green-600 text-xs">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Ngày TT',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 80,
      render: (date: string) => (
        <span className="text-xs">{dayjs(date).format('DD/MM')}</span>
      ),
    },
    {
      title: 'TT',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 60,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="text-xs px-1">
          {status === 'COMPLETED' ? 'OK' : 'Chờ'}
        </Tag>
      ),
    },
    {
      title: 'NV',
      dataIndex: 'employee',
      key: 'employee',
      width: 80,
      render: (employee: any) => (
        <span className="text-xs">{employee?.name || 'N/A'}</span>
      ),
    },
    {
      title: 'Tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 90,
      render: (date: string) => (
        <span className="text-xs">{dayjs(date).format('DD/MM HH:mm')}</span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      render: (_, record: PaymentResponse) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPayment(record)}
            title="Sửa"
          />
          <Popconfirm
            title="Xóa thanh toán?"
            onConfirm={() => handleDeletePayment(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okType="danger"
          >
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              danger
              title="Xóa"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Table columns for deleted payments (no actions)
  const deletedPaymentColumns: ColumnsType<PaymentResponse> = [
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (amount: number) => (
        <span className="font-semibold text-green-600 text-xs">
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      title: 'Ngày TT',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 80,
      render: (date: string) => (
        <span className="text-xs">{dayjs(date).format('DD/MM')}</span>
      ),
    },
    {
      title: 'TT',
      dataIndex: 'originalStatus',
      key: 'originalStatus',
      width: 80,
      render: (status: string) => (
        <Tag color="red" className="text-xs px-1">
          Đã xóa
        </Tag>
      ),
    },
    {
      title: 'NV',
      dataIndex: 'employee',
      key: 'employee',
      width: 80,
      render: (employee: any) => (
        <span className="text-xs">{employee?.name || 'N/A'}</span>
      ),
    },
    {
      title: 'Tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 90,
      render: (date: string) => (
        <span className="text-xs">{dayjs(date).format('DD/MM HH:mm')}</span>
      ),
    },
    {
      title: 'Người xóa',
      dataIndex: 'deletedByName',
      key: 'deletedByName',
      width: 120,
      render: (deletedByName: string | null) => deletedByName || '-',
    },
    {
      title: 'Xóa',
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 90,
      render: (date: string) => (
        <span className="text-xs">{dayjs(date).format('DD/MM HH:mm')}</span>
      ),
    },
  ];

  // Calculate total payment amount
  const totalPaymentAmount = payments
    .filter(
      (payment) => payment.originalStatus === 'COMPLETED' && !payment.deletedAt
    )
    .reduce((total, payment) => total + payment.amount, 0);

  // Calculate total amount (thành tiền) from customer fee
  const totalAmount = transaction
    ? (() => {
        const { amount, feeForCustomer, customerType } = transaction;
        if (!feeForCustomer) return 0;

        if (customerType === 'vn') {
          // Phí VND: Thành tiền = Phí KH (đã là số tiền cụ thể)
          return feeForCustomer;
        } else {
          // Phí %: Thành tiền = Số tiền rút × (Phí KH %)
          return amount * (feeForCustomer / 100);
        }
      })()
    : 0;

  // Calculate remaining amount = totalAmount - totalPaymentAmount
  const remainingAmount = totalAmount - totalPaymentAmount;

  return (
    <>
      <Modal
        title={<span className="text-sm">Thanh toán GD rút tiền</span>}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel} size="small">
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleSubmit}
            size="small"
          >
            Ghi nhận
          </Button>,
        ]}
        width={900}
        destroyOnClose
      >
        {transaction && (
          <div className="mb-3 p-2 bg-gray-50 rounded">
            <h4 className="font-semibold mb-1 text-xs">Thông tin GD</h4>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div>
                <span className="text-gray-600">ID:</span>
                <div className="font-medium">#{transaction.id}</div>
              </div>
              <div>
                <span className="text-gray-600">Thẻ:</span>
                <div className="font-medium">
                  {transaction.creditCard?.cardNumber
                    ? formatCardNumber(transaction.creditCard.cardNumber)
                    : 'N/A'}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Số tiền rút:</span>
                <div className="font-medium text-blue-600">
                  {formatCurrency(transaction.amount)}
                </div>
              </div>
              <div>
                <span className="text-gray-600">Cần thanh toán:</span>
                <div className="font-medium text-purple-600">
                  {formatCurrency(totalAmount)}
                </div>
              </div>
            </div>
            {totalPaymentAmount > 0 && (
              <div className="mt-2 pt-1 border-t text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Đã TT:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(totalPaymentAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Còn lại:</span>
                  <span className="font-semibold text-orange-600">
                    {formatCurrency(remainingAmount)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payments with Tabs */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-2">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="flex-1"
              size="small"
            >
              <TabPane tab="Hiện tại" key="active" />
              <TabPane tab="Đã xóa" key="deleted" />
            </Tabs>
            <h4 className="text-xs font-semibold text-gray-700 ml-2">
              Lịch sử TT
            </h4>
          </div>

          <div>
            {activeTab === 'active' && (
              <Spin spinning={paymentsLoading}>
                <Table
                  columns={paymentColumns}
                  dataSource={payments}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  locale={{
                    emptyText: 'Chưa có thanh toán',
                  }}
                  scroll={{ x: 500 }}
                />
              </Spin>
            )}

            {activeTab === 'deleted' && (
              <>
                <div className="mb-2 flex justify-end">
                  <Button
                    type="text"
                    size="small"
                    onClick={loadDeletedPayments}
                    loading={deletedPaymentsLoading}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                  >
                    Làm mới
                  </Button>
                </div>

                {deletedPaymentsError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                    <div className="font-medium">Lỗi tải dữ liệu:</div>
                    <div>{deletedPaymentsError}</div>
                  </div>
                )}

                <Spin spinning={deletedPaymentsLoading}>
                  <Table
                    columns={deletedPaymentColumns}
                    dataSource={deletedPayments}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    locale={{
                      emptyText: !deletedPaymentsError ? (
                        <div className="text-center py-4">
                          <div className="text-gray-400 text-xs">
                            Không có TT đã xóa
                          </div>
                        </div>
                      ) : null,
                    }}
                    scroll={{ x: 530 }}
                  />
                </Spin>
              </>
            )}
          </div>
        </div>

        <Divider className="my-3" />

        <h4 className="font-semibold mb-2 text-sm">Ghi nhận TT mới</h4>

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            paymentDate: dayjs(),
            amount: 0,
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="amount"
              label={<span className="text-xs">Số tiền</span>}
              rules={[
                { required: true, message: 'Vui lòng nhập số tiền' },
                { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="small"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                placeholder="Số tiền"
                addonAfter="VND"
              />
            </Form.Item>

            <Form.Item
              name="paymentDate"
              label={<span className="text-xs">Ngày TT</span>}
              rules={[
                { required: true, message: 'Vui lòng chọn ngày thanh toán' },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                size="small"
                format="DD/MM/YYYY"
                placeholder="Chọn ngày"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="notes"
            label={<span className="text-xs">Ghi chú</span>}
          >
            <Input.TextArea
              rows={2}
              placeholder="Ghi chú (tùy chọn)"
              size="small"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Payment Modal */}
      <Modal
        title={<span className="text-sm">Sửa thanh toán</span>}
        open={editModalVisible}
        onCancel={handleEditCancel}
        footer={[
          <Button key="cancel" onClick={handleEditCancel} size="small">
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleUpdatePayment}
            size="small"
          >
            Cập nhật
          </Button>,
        ]}
        width={400}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="amount"
              label={<span className="text-xs">Số tiền</span>}
              rules={[
                { required: true, message: 'Vui lòng nhập số tiền' },
                { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                size="small"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                placeholder="Số tiền"
                addonAfter="VND"
              />
            </Form.Item>

            <Form.Item
              name="paymentDate"
              label={<span className="text-xs">Ngày TT</span>}
              rules={[
                { required: true, message: 'Vui lòng chọn ngày thanh toán' },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                size="small"
                format="DD/MM/YYYY"
                placeholder="Chọn ngày"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="notes"
            label={<span className="text-xs">Ghi chú</span>}
          >
            <Input.TextArea
              rows={2}
              placeholder="Ghi chú (tùy chọn)"
              size="small"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default WithdrawalPaymentModal;
